/**
 * Gestión de licencias (mockup "Clappers en licencia" → "Gestionar licencias"):
 * dialog con búsqueda + tabla paginada para marcar/desmarcar personas en
 * licencia durante la ventana del D-Talk. Opera sobre TODAS las filas (no
 * depende de los filtros del dashboard).
 */
import { currentLocale } from '@/lib/locale';
import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ChevronLeft, ChevronRight, UserMinus, UserPlus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';

export interface DTalkLicenseRow {
  employeeEmail: string;
  employeeName: string;
  employeeLastName: string;
  employeeArea: string | null;
  employeeCountry?: string | null;
  peopleManager?: string | null;
  eligibilityStatus: string;
  licenseExcludedBy?: string | null;
  licenseExcludedAt?: string | null;
}

const PAGE_SIZE = 8;

export function DTalkLicenseManagerDialog({
  open,
  onOpenChange,
  rows,
  onToggleLicense,
  pending,
  description,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rows: DTalkLicenseRow[];
  onToggleLicense: (email: string, exclude: boolean) => void;
  pending?: boolean;
  /** Texto bajo el título; por defecto el de D-Talk (el Mapa de Talento pasa el suyo). */
  description?: string;
}) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [onlyLicense, setOnlyLicense] = useState(false);
  const [page, setPage] = useState(0);

  useEffect(() => { setPage(0); }, [search, onlyLicense]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return rows
      .filter((r) => (onlyLicense ? r.eligibilityStatus === 'license' : true))
      .filter((r) => !s || `${r.employeeName} ${r.employeeLastName} ${r.employeeEmail}`.toLowerCase().includes(s));
  }, [rows, search, onlyLicense]);

  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('performance.dtalklicensemanagerdialog.clappers_en_licencia')}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground -mt-2">
          {description ?? t('performance.dtalklicensemanagerdialog.marca_o_desmarca_clappers_que_se')}
        </p>
        <div className="flex items-center gap-2">
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('performance.dtalklicensemanagerdialog.buscar_por_nombre_o_email')} className="text-sm" />
          <Button
            variant={onlyLicense ? 'default' : 'outline'} size="sm" className="shrink-0 h-9 text-xs"
            onClick={() => setOnlyLicense((v) => !v)}
          >
            {t('performance.dtalklicensemanagerdialog.solo_en_licencia')}
          </Button>
        </div>
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground border-b bg-muted/40">
                <th className="py-2 px-3 font-semibold">{t('performance.dtalklicensemanagerdialog.nombre')}</th>
                <th className="py-2 px-3 font-semibold hidden sm:table-cell">{t('performance.dtalklicensemanagerdialog.area')}</th>
                <th className="py-2 px-3 font-semibold hidden md:table-cell">{t('performance.dtalklicensemanagerdialog.marcado_por')}</th>
                <th className="py-2 px-3 font-semibold">{t('performance.dtalklicensemanagerdialog.estado')}</th>
                <th className="py-2 px-3"></th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map((r) => {
                const inLicense = r.eligibilityStatus === 'license';
                return (
                  <tr key={r.employeeEmail} className="border-b last:border-0">
                    <td className="py-2 px-3">
                      <p className="font-medium">{r.employeeName} {r.employeeLastName}</p>
                      <p className="text-xs text-muted-foreground">{r.employeeEmail}</p>
                    </td>
                    <td className="py-2 px-3 text-muted-foreground hidden sm:table-cell">{r.employeeArea || '—'}</td>
                    <td className="py-2 px-3 text-xs text-muted-foreground hidden md:table-cell">
                      {inLicense && r.licenseExcludedBy ? (
                        <>
                          {r.licenseExcludedBy}
                          {r.licenseExcludedAt ? ` · ${new Date(r.licenseExcludedAt).toLocaleDateString(currentLocale())}` : ''}
                        </>
                      ) : '—'}
                    </td>
                    <td className="py-2 px-3">
                      <span className={cn('inline-flex rounded-full px-2 py-0.5 text-xs font-semibold',
                        inLicense ? 'bg-violet-100 text-violet-700 dark:bg-violet-950/40 dark:text-violet-300' : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300')}>
                        {inLicense ? t('performance.dtalklicensemanagerdialog.en_licencia') : t('performance.dtalklicensemanagerdialog.activa')}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-right">
                      <Button
                        variant="outline" size="sm" className="h-7 gap-1 px-2 text-xs" disabled={pending}
                        onClick={() => onToggleLicense(r.employeeEmail, !inLicense)}
                      >
                        {inLicense ? <><UserPlus className="h-3.5 w-3.5" />{t('performance.dtalklicensemanagerdialog.reactivar')}</> : <><UserMinus className="h-3.5 w-3.5" />{t('performance.dtalklicensemanagerdialog.marcar_licencia')}</>}
                      </Button>
                    </td>
                  </tr>
                );
              })}
              {pageRows.length === 0 && (
                <tr><td colSpan={5} className="py-8 text-center text-sm text-muted-foreground">{t('performance.dtalklicensemanagerdialog.sin_resultados')}</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>
            {t('performance.dtalklicensemanagerdialog.mostrando', {
              from: filtered.length === 0 ? 0 : page * PAGE_SIZE + 1,
              to: Math.min((page + 1) * PAGE_SIZE, filtered.length),
              total: filtered.length,
            })}
          </span>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" aria-label={t('common.a11y.previous_page')} className="h-7 w-7" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="px-1">{page + 1} / {pages}</span>
            <Button variant="ghost" size="icon" aria-label={t('common.a11y.next_page')} className="h-7 w-7" disabled={page >= pages - 1} onClick={() => setPage((p) => p + 1)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
